// @flow
import React, { useCallback } from 'react';
import { isNil } from 'lodash';
import { Button, Icon } from '@elements';
import { fsPrintf, openBillingPage } from '@helpers/helpers';
import './StoreReview.scss';

// Apple's own page for the subscriptions bought through the App Store
export const APP_STORE_SUBSCRIPTIONS_URL = 'https://apps.apple.com/account/subscriptions';

type PlanProps = {
  subscriptionStatus: ?string,
  trialEndsInDays: ?number,
  onLogout: Function,
  _: Function,
};

// /planos for an iOS build in App Store review (APP4_IOS_REVIEW_VERSIONS): the account's state, with
// no plans, prices or way to subscribe (App Review guideline 3.1.1)
export const StoreReviewPlan = ({ subscriptionStatus, trialEndsInDays, onLogout, _ }: PlanProps) => {
  const trialing = subscriptionStatus === 'trialing';
  let text = _('store_review/ended_text');
  if (trialing) {
    const left = Math.max(0, Math.round(Number(trialEndsInDays) || 0));
    text = _('label/trialing_counter', null, fsPrintf(left));
    if (isNil(trialEndsInDays)) text = '';
    else if (left === 0) text = _('label/trialing_counter_today');
    else if (left === 1) text = _('label/trialing_counter_one');
  }

  return (
    <div className="storeReview">
      <span className="storeReview-icon"><Icon iconName={trialing ? 'time' : 'lock'} size={26} /></span>
      <h1>{trialing ? _('store_review/trialing_title') : _('store_review/ended_title')}</h1>
      {text && <p>{text}</p>}
      <div className="storeReview-actions">
        <Button className="primary" text={_('store_review/support')} intercom />
        {!trialing && <Button text={_('store_review/logout')} onClick={onLogout} />}
      </div>
    </div>
  );
};

type SubscriptionProps = {
  planName: ?string,
  paymentMethod: ?string,
  warning: ?string,
  // The app blocks the account (advancedData.blocked), whatever the plan's request said
  blocked?: boolean,
  _: Function,
};

// The API's warnings for an account that lost access, or is about to, until it pays
const INACTIVE_WARNINGS = ['cc_past_due', 'boleto_past_due', 'canceled', 'expired'];
const isInactive = (warning: ?string) => Boolean(warning) && (INACTIVE_WARNINGS.includes(warning) || String(warning).endsWith('_expired_charge'));

// Meu Plano for an iOS build in App Store review: the plan's name, and nothing about paying for it.
// Whoever pays through the App Store manages it there.
export const StoreReviewSubscription = ({ planName, paymentMethod, warning, blocked = false, _ }: SubscriptionProps) => {
  const viaAppStore = paymentMethod === 'apple';
  let text = viaAppStore ? _('store_review/app_store_text') : _('store_review/active_text');
  if (blocked || isInactive(warning)) text = _('store_review/inactive_text');
  const openAppStore = useCallback(() => openBillingPage(APP_STORE_SUBSCRIPTIONS_URL), []);

  return (
    <div className="storeReview left">
      <h1>{planName || _('store_review/plan')}</h1>
      <p>{text}</p>
      <div className="storeReview-actions">
        {viaAppStore && <Button className="primary" text={_('store_review/app_store_manage')} onClick={openAppStore} />}
        <Button text={_('store_review/support')} intercom />
      </div>
    </div>
  );
};
